import React from 'react';
import SparkMD5 from 'spark-md5';
import uuid from 'uuid';
import { parse } from 'qs';
import CommentBox from './CommentBox.jsx';
import Divider from './Divider.jsx';

import firebase from '../utils/firebase';
import { defaultApp, projectApp } from '../utils/firebase';
import { VOTE_NONE, VOTE_UP, VOTE_DOWN, EDITABLE_DURATION, CACHE_CONTROL } from '../utils/constants';
import logo from '../images/logo.svg';

import './App.css';

const pendingCommentsStorage = projectApp.storage('gs://pending-comments.storage.commentbox.io');
const flaggedCommentsStorage = projectApp.storage('gs://flagged-comments.storage.commentbox.io');
const commentVotesStorage = projectApp.storage('gs://comment-votes.storage.commentbox.io');

const firestore = projectApp.firestore();

// firestore.settings({ timestampsInSnapshots: true });

class NetworkError extends Error {

    constructor(message, code) {
        super(message);
        this.code = code;
    }
}


class App extends React.Component {

    firestorePagesRef = firestore.collection('pages');
    firestoreUserPreferencesRef = firestore.collection('user-preferences');
    firestoreUserPageProfilesRef = firestore.collection('user-page-profiles');

    storagePendingCommentsRef = pendingCommentsStorage
        .ref()
        .child(this.props.projectId)
        .child(this.props.pageId);

    storageFlaggedCommentsRef = flaggedCommentsStorage
        .ref()
        .child(this.props.projectId)
        .child(this.props.pageId);

    storageCommentVotesRef = commentVotesStorage
        .ref()
        .child(this.props.projectId)
        .child(this.props.pageId);

    state = Object.assign(this.constructor.defaultState, {
        defaultUserPayload: this.props.ssoUserPayload
    });

    receiveMessage = e => {

        const urlLocation = document.createElement('a');
        const parentLocation = document.createElement('a');
        urlLocation.href = this.props.url;
        parentLocation.href = e.origin;

        if (e.data && e.source === window.parent && urlLocation.host === parentLocation.host) {

            try {

                const data = JSON.parse(e.data);
                const { event, payload } = data;

                switch (event) {
                    case 'sso-user-payload':

                        if (!this.state.requestingUserPayload) {
                            throw new Error(`Receiving a message we didn't ask for! This may be a sign that your client-side scripts have been compromised.`);
                        }
                        return this.singleSignOn(payload);

                    case 'sso-user-payload-error':

                        if (!this.state.requestingUserPayload) {
                            throw new Error(`Receiving a message we didn't ask for! This may be a sign that your client-side scripts have been compromised.`);
                        }

                        return this.setState({ requestingUserPayload: false });
                }

            } catch (err) {

            }
        }
    };

    sendMessage = (event, payload = '') => {

        if (window.parent) {

            window.parent.postMessage(JSON.stringify({ event, payload, id: this.props.boxId }), '*');
        }
    };

    sendResizeEvent = e => {

        if (window.requestAnimationFrame) {

            return window.requestAnimationFrame(() => {
                this.sendMessage('resize', Math.max(document.body.scrollHeight, 100));
            });
        }
        return setTimeout(() => {
            this.sendMessage('resize', Math.max(document.body.scrollHeight, 100));
        }, 10);
    };

    scrollToTlc = top => {

        this.setState({ scrolledToTlc: true }, () => {

            this.sendMessage('tlc', top);
        });
    };

    authStateChanged = user => {

        if (this.props.sso && this.state.defaultUserPayload && !user) {

            const { defaultUserPayload } = this.state;

            return this.promiseState({ defaultUserPayload: null }).then(() => this.singleSignOn(defaultUserPayload));
        }

        const userId = user ? user.uid : null;
        if (user && !user.email) {

            return user.delete().then(() => this.promiseState({ emailError: true, reAuth: false }));
        }

        const p = user ?
            this.promiseState({ emailError: false, isLoggedIn: true })
                .then(() => this.login())
                .catch(err => this.promiseState({ loadingError: err }))
            : this.promiseState(Object.assign({}, this.constructor.defaultState, { scrolledToTlc: this.state.scrolledToTlc }));

        p.then(auth => {

            const toLoad = [
                this.getComments(),
                this.getCommentVotes(),
                this.getPage(auth && auth.userIsModerator),
                this.getUserPageProfile(auth ? userId : null),
                this.getUserPreferences(auth ? userId : null)
            ];

            return Promise.all(toLoad)
                .then((
                    [
                        comments,
                        commentVotes,
                        page,
                        userPageProfile,
                        userPreferences
                    ]
                ) => {

                    return this.correctCommentVotes(commentVotes, userPageProfile)
                        .then(commentVotes => {

                            return this.correctComments(comments, userPageProfile, commentVotes)
                                .then(({comments, editableComments}) => {

                                    return { comments, commentVotes, editableComments };
                                });
                        })
                        .then(({ comments, commentVotes, editableComments }) => {

                            const { isLoggedIn, onUser, scrolledToTlc, loadingError, emailError } = this.state;

                            const state = Object.assign(this.constructor.defaultState, {
                                comments,
                                commentVotes,
                                editableComments,
                                page,
                                userPageProfile,
                                userPreferences,
                                isLoggedIn,
                                loadingError,
                                emailError,
                                onUser,
                                scrolledToTlc,
                                loading: false
                            });

                            if (auth) {

                                const {
                                    userIsModerator,
                                    userIsBlocked,
                                    userIsBlockedAnonymous,
                                    isModerationPlus
                                } = auth;

                                const {
                                    uid: userId,
                                    displayName: userDisplayName,
                                    photoURL: userPhotoURL,
                                    providerData: userProviderData
                                } = user;

                                Object.assign(state, {
                                    userId,
                                    userDisplayName,
                                    userPhotoURL,
                                    userProviderData,
                                    userIsModerator,
                                    userIsBlocked,
                                    userIsBlockedAnonymous,
                                    isModerationPlus,
                                    onUser: null
                                });
                            }

                            return this.promiseState(state).then(() => {

                                this.sendMessage('count', comments.filter(c => !!c.body).length);

                                if (user && onUser) {
                                    onUser.resolve();
                                }
                            });
                        });
                });

        })
            .catch(loadingError => {

                console.error(loadingError);

                this.setState({ loadingError });
            });

    };

    componentDidMount() {

        window.addEventListener('resize', this.sendResizeEvent);
        window.addEventListener('message', this.receiveMessage);

        this.unregisterAuthObserver = defaultApp.auth().onAuthStateChanged(this.authStateChanged);
    }

    componentWillUnmount() {

        window.removeEventListener('resize', this.sendResizeEvent);
        window.removeEventListener('message', this.receiveMessage);

        this.unregisterAuthObserver();
    }

    componentDidUpdate() {

        this.sendResizeEvent();
    }

    promiseState(state) {

        return new Promise(resolve => {

            this.setState(state, resolve);
        });
    }

    promiseStateWithUndo(originalState, newState, action) {

        return this.promiseState(newState).then(action).catch(error => {

            return this.promiseState(originalState)
                .then(() => {

                    return this.promiseState({ error });
                })
                .then(() => {

                    throw error;
                });
        });
    }

    correctComments = (comments, userPageProfile, commentVotes) => {

        const newComments = [];
        const foundCommentIds = {};
        const toFetch = [];

        comments.forEach(comment => {

            foundCommentIds[comment.id] = comment.version;

            const pieces = comment.id.split('/');
            while (pieces.length > 1) {
                pieces.pop();
                const parentCommentId = pieces.join('/');
                if (!foundCommentIds[parentCommentId]) {
                    foundCommentIds[parentCommentId] = false;
                }
            }

            comment.hash = SparkMD5.hash(comment.id);
            comment.rank = this.constructor.best(commentVotes.up[comment.id] || 0, commentVotes.down[comment.id] || 0);

            if (!userPageProfile) {
                return newComments.push(comment);
            }

            if (userPageProfile.deletedComments[comment.id]) {

                foundCommentIds[comment.id] = false;
                return;
            }

            if (!userPageProfile.comments[comment.id]) {
                return newComments.push(comment);
            }

            if (userPageProfile.comments[comment.id] === comment.version) {

                return newComments.push(comment);
            }
        });

        Object.keys(foundCommentIds).forEach(id => {

            if (!foundCommentIds[id]) {

                newComments.push({
                    id,
                    hash: SparkMD5.hash(id),
                    rank: this.constructor.best(commentVotes.up[id] || 0, commentVotes.down[id] || 0)
                });
            }
        });

        if (!userPageProfile) {

            return Promise.resolve(comments);
        }

        Object.keys(userPageProfile.comments).forEach(id => {

            if (userPageProfile.comments[id] !== foundCommentIds[id] && !userPageProfile.deletedComments[id]) {
                toFetch.push(id);
            }
        });

        return Promise.all(toFetch.map(commentId => {

            return this.storagePendingCommentsRef.child(commentId).getMetadata()
                .then(({ timeCreated, updated, customMetadata }) => {

                    if (customMetadata.anonymous === '1') {
                        delete customMetadata.userId;
                        delete customMetadata.userDisplayName;
                        delete customMetadata.userPhotoURL;
                    }

                    return Object.assign({}, customMetadata, {
                        createdAt: Date.parse(timeCreated),
                        updatedAt: Date.parse(updated),
                        owned: true,
                        hash: SparkMD5.hash(commentId),
                        rank: this.constructor.best(commentVotes.up[commentId] || 0, commentVotes.down[commentId] || 0)
                    });
                })
                .catch(err => {

                    return { id: commentId };
                });

        })).then(missingComments => {

            return missingComments.concat(newComments.filter(comment => {

                return !missingComments.find(c => c.id === comment.id);
            }));

        }).then(comments => {

            const replies = {};
            const editableComments = {};

            comments.forEach(comment => {

                if (userPageProfile.comments[comment.id] || (this.state.userId && (comment.userId === this.state.userId))) {
                    comment.owned = true;
                }

                const duration = Date.now() - Number(comment.createdAt);

                if (comment.body && comment.owned && duration < EDITABLE_DURATION) {

                    editableComments[comment.id] = setTimeout(() => {

                        this.clearEditableComment(comment.id);

                    }, EDITABLE_DURATION - duration);
                }
            });

            return { editableComments, comments };
        });
    };

    correctCommentVotes = (commentVotesOld, userPageProfile) => {

        const commentVotes = {
            up: Object.assign({}, commentVotesOld.up),
            down: Object.assign({}, commentVotesOld.down)
        };

        if (userPageProfile) {

            Object.keys(userPageProfile.commentsVotedOn || {}).forEach(commentId => {

                const vote = userPageProfile.commentsVotedOn[commentId];

                if (vote === VOTE_UP && !commentVotes.up[commentId]) {
                    commentVotes.up[commentId] = 1;
                } else if (vote === VOTE_DOWN && !commentVotes.down[commentId]) {
                    commentVotes.down[commentId] = 1;
                }
            });
        }

        return Promise.resolve(commentVotes);
    };

    clearEditableComment =commentIdToClear => {

        this.setState(state => {

            return {
                editableComments: Object.keys(state.editableComments)
                    .reduce((editableComments, commentId) => {

                        if (commentId !== commentIdToClear) {
                            editableComments[commentId] = state.editableComments[commentId];
                        }
                        return editableComments;
                    }, {})
            };
        });
    };

    getUser = () => {


        const getUserPromise = new Promise((resolve, reject) => {

            if (this.state.userId) {
                return resolve();
            }

            setTimeout(() => {

                this.promiseState({
                    getUserPromise,
                    onUser: {
                        resolve: () => {

                            resolve();
                            this.setState({ getUserPromise: null, onUser: null });

                        },
                        reject: (err) => {

                            reject(err);
                            this.setState({ getUserPromise: null, onUser: null });
                        }
                    }
                })

            }, 0);

        }).catch((err) => {
            if (err) {
                throw err;
            }
        });

        return getUserPromise;
    };

    getComments = () => {

        const { projectId, pageId } = this.props;
        const url = `https://total-comments.storage.commentbox.io/${projectId}/${pageId}.json`

        return fetch(url, { cache: 'default', mode: 'cors' }).then(response => {

            if(!response.ok) {
                throw new Error();
            }
            return response.json().then(({ data }) => data);

        }).catch(() => []);
    };

    getCommentVotes = () => {

        const { projectId, pageId } = this.props;
        const url = `https://total-comment-votes.storage.commentbox.io/${projectId}/${pageId}.json`

        return fetch(url, { cache: 'default', mode: 'cors' }).then(response => {

            if(!response.ok) {
                throw new Error();
            }
            return response.json().then(({ data }) => data);

        }).catch(err => ( { up: {}, down: {} } ));

    };

    getPage = (userIsModerator) => {

        const { projectId, pageId } = this.props;
        if (userIsModerator) {
            return this.firestorePagesRef.doc(`${projectId}_${pageId}`).get()
                .then(doc => doc.data() || {})
                .then(page => Object.assign({}, this.constructor.defaultPage, page));
        }
        const url = `https://page-settings.storage.commentbox.io/${projectId}/${pageId}.json`

        return fetch(url, { cache: 'default', mode: 'cors' }).then(response => {

            if(!response.ok) {
                throw new Error();
            }
            return response.json().then(({ restrictions, pinnedComments }) => {
                return {
                    status: restrictions || null,
                    pinnedComments: pinnedComments.reduce((map, commentId) => {
                        return { ...map, [commentId]: '1' };
                    }, {}),
                }
            });

        }).catch(err => this.constructor.defaultPage);
    };

    getUserPageProfile = userId => {

        if (!userId) {
            return Promise.resolve(this.constructor.defaultUserPageProfile);
        }

        const { projectId, pageId } = this.props;

        return this.firestoreUserPageProfilesRef.doc(`${projectId}_${pageId}_${userId}`).get()
            .then(doc => doc.data() || {})
            .then(userPageProfile => Object.assign({}, this.constructor.defaultUserPageProfile, userPageProfile));
    };

    getUserPreferences = userId => {

        if (!userId) {
            return Promise.resolve(this.constructor.defaultUserPreferences);
        }

        return this.firestoreUserPreferencesRef.doc(userId).get()
            .then(doc => doc.data() || {})
            .then(userPreferences => Object.assign({}, this.constructor.defaultUserPreferences, userPreferences));
    };

    updateUserPageProfile = mergeFn => {

        return firestore.runTransaction(transaction => {

            const { projectId, pageId } = this.props;
            const { userId } = this.state;

            const userPageProfileRef = this.firestoreUserPageProfilesRef.doc(`${projectId}_${pageId}_${userId}`);

            // This code may get re-run multiple times if there are conflicts.

            return transaction.get(userPageProfileRef).then(doc => {

                const userPageProfile = doc.data() || this.constructor.defaultUserPageProfile;

                try {

                    const update = Object.assign(
                        {},
                        this.constructor.defaultUserPageProfile,
                        mergeFn(userPageProfile),
                        { projectId, pageId, userId }
                    );

                    transaction.set(userPageProfileRef, update);

                    return update;

                } catch(err) {

                    return Promise.reject(err);
                }
            });

        }).then(userPageProfile => {

            return this.promiseState({ userPageProfile });
        });
    };

    updateUserPreferences = preferences => {

        const { userId, userPreferences } = this.state;

        const originalState = { userPreferences };
        const newState = {
            userPreferences: Object.assign({}, userPreferences, preferences)
        };

        return this.promiseStateWithUndo(originalState, newState, () => {

            return this.firestoreUserPreferencesRef.doc(userId).set(newState.userPreferences);
        });
    };

    changeRemember = remember => {

        this.setState({ remember });
    };

    changeReAuth = reAuth => {

        this.setState({ reAuth });
    };

    changeUserPreferences = preferences => {

        this.setState({
            userPreferences: Object.assign({}, this.state.userPreferences, preferences)
        });
    };

    updateUserProfile = (displayName, photoURL = '') => {

        return defaultApp.auth().currentUser.updateProfile({ displayName, photoURL })
            .then(() => {

                const projectUser = projectApp.auth().currentUser;
                if (!projectUser) {
                    return null;
                }
                return projectUser.getIdToken(/* forceRefresh */ true)
            })
            .then(() => {

                return this.promiseState({ userDisplayName: displayName, userPhotoURL: photoURL });
            });
    };

    postComment = (body, parentCommentId) => {


        const newId = uuid.v4();
        const id = parentCommentId ? `${parentCommentId}/${newId}` : newId;
        const {
            userId,
            userDisplayName,
            userPhotoURL,
            editableComments,
            userPreferences: { anonymous, subscribe }
        } = this.state;
        const { url, tlcParam } = this.props;

        const cacheControl = CACHE_CONTROL;
        const customMetadata = {
            id,
            body,
            userId,
            tlcParam,
            url,
            userDisplayName: userDisplayName || '',
            userPhotoURL: userPhotoURL || '',
            anonymous: anonymous ? '1' : '0',
            subscribe: subscribe ? '1' : '0',
            version: '0',
        };

        const now = Date.now();

        const comment = Object.assign({
            createdAt: now,
            updatedAt: now,
            isNew: true,
            owned: true,
            hash: SparkMD5.hash(id),
            parentCommentId,
            replies: []
        }, customMetadata);

        if (comment.anonymous === '1') {
            delete comment.userId;
            delete comment.userDisplayName;
            delete comment.userPhotoURL;
        }

        return this.updateUserPageProfile(userPageProfile => {

            Object.assign(userPageProfile.comments, { [comment.id]: '0' });

            return userPageProfile;

        })
            .then(() => {

                return this.storagePendingCommentsRef.child(id)
                    .putString('', firebase.storage.StringFormat.RAW, {
                        cacheControl,
                        customMetadata
                    });
            })
            .then(() => {

                return this.updateUserPreferences({ anonymous, subscribe }).catch(err => {});
            })
            .then(() => {

                return this.promiseState({
                    comments: [ comment ].concat(this.state.comments),
                    editableComments: Object.assign({
                        [id]: setTimeout(() => {

                            this.clearEditableComment(id);

                        }, EDITABLE_DURATION)
                    }, editableComments)
                });
            });
    };

    editComment = (body, id) => {

        const {
            userId,
            userDisplayName,
            userPhotoURL,
            userPreferences: {
                anonymous,
                subscribe
            }
        } = this.state;
        const { url, tlcParam } = this.props;

        return this.updateUserPageProfile(userPageProfile => {

            const version = Number(userPageProfile.comments[id]) + 1;

            if (version > 9) {
                throw new Error('This comment has been edited too many times.');
            }

            userPageProfile.comments[id] = `${version}`;

            return userPageProfile;

        })
            .then(() => {

                return this.updateUserPreferences({ anonymous, subscribe }).catch(err => {});
            })
            .then(() => {

                const { userPageProfile } = this.state;
                const cacheControl = CACHE_CONTROL;
                const customMetadata = {
                    id,
                    body,
                    userId,
                    url,
                    tlcParam,
                    userDisplayName: userDisplayName || '',
                    userPhotoURL: userPhotoURL || '',
                    anonymous: anonymous ? '1' : '0',
                    subscribe: subscribe ? '1' : '0',
                    version: userPageProfile.comments[id]
                };

                return this.storagePendingCommentsRef.child(id).updateMetadata({ cacheControl, customMetadata })
                    .catch(err => {
                        if (err.code !== 'storage/object-not-found') {
                            throw err;
                        }
                    })
                    .then(() => {

                        return this.promiseState({
                            comments: this.state.comments.map(comment => {

                                if (comment.id !== id) {
                                    return comment;
                                }

                                return Object.assign({}, comment, customMetadata);
                            })
                        });
                    });
            });
    };

    removeComment = id => {

        const { comments, userPageProfile } = this.state;
        let commentToDelete = null;
        const newComments = this.state.comments.map(comment => {

            if (comment.id !== id) {
                return comment;
            } else {
                commentToDelete = comment;
            }

            return { id };
        });

        const newDeletedComments = Object.assign({}, userPageProfile.deletedComments, {
            [id]: commentToDelete.owned ? '0' : '1'
        });

        const originalState = { comments, userPageProfile };
        const newState = {
            userPageProfile: Object.assign({}, userPageProfile, {
                deletedComments: newDeletedComments,
            }),
            comments: newComments
        };

        return this.promiseStateWithUndo(originalState, newState, () => {

            return this.storagePendingCommentsRef.child(id)
                .delete()
                .catch(err => {
                    if (err.code !== 'storage/object-not-found') {
                        throw err;
                    }
                })
                .then(() => {

                    return this.updateUserPageProfile(userPageProfile => {

                        Object.assign(userPageProfile.deletedComments, {
                            [id]: commentToDelete.owned ? '0' : '1'
                        });

                        userPageProfile.comments = Object.keys(userPageProfile.comments).reduce((comments, commentId) => {

                            if (id !== commentId) {
                                comments[commentId] = userPageProfile.comments[commentId];
                            }

                            return comments;
                        }, {});

                        return userPageProfile;
                    });
                });
        });

    };

    flagComment = id => {

        const { userId, userPageProfile } = this.state;

        const newFlaggedComments = Object.assign({}, userPageProfile.flaggedComments, {
            [id]: '0'
        });

        const customMetadata = { commentId: id };

        const originalState = { userPageProfile };

        const newState = {
            userPageProfile: Object.assign({}, userPageProfile, {
                flaggedComments: newFlaggedComments,
            })
        };

        return this.promiseStateWithUndo(originalState, newState, () => {

            return this.updateUserPageProfile(userPageProfile => {

                Object.assign(userPageProfile.flaggedComments, { [id]: '0' });

                return userPageProfile;
            })
                .then(() => {

                    return this.storageFlaggedCommentsRef.child(userId).child(id)
                        .putString('', firebase.storage.StringFormat.RAW, { customMetadata })
                        .catch(err => console.error(err));
                });
        });

    };

    commentVote = (commentId, vote) => {

        const { userId, commentVotes, userPageProfile } = this.state;
        const version = Number(userPageProfile.commentVoteVersions[commentId] || '-1') + 1;

        if (version > 9) {
            throw new Error('This comment has been voted on too many times.');
        }

        const newCommentVoteVersions = Object.assign({}, userPageProfile.commentVoteVersions, {
            [commentId]: `${version}`
        });
        const newCommentVotes = {
            up: Object.assign({}, commentVotes.up),
            down: Object.assign({}, commentVotes.down)
        };
        const newCommentsVotedOn = Object.assign({}, userPageProfile.commentsVotedOn);
        const value = this.constructor.calculateCommentVote(commentId, vote, newCommentVotes, newCommentsVotedOn);

        const customMetadata = {
            commentId,
            value,
            version: newCommentVoteVersions[commentId]
        };

        const originalState = { commentVotes, userPageProfile };

        const newState = {
            commentVotes: newCommentVotes,
            userPageProfile: Object.assign({}, userPageProfile, {
                commentsVotedOn: newCommentsVotedOn,
                commentVoteVersions: newCommentVoteVersions
            })
        };

        return this.promiseStateWithUndo(originalState, newState, () => {

            return this.updateUserPageProfile(userPageProfile => {

                Object.assign(userPageProfile.commentsVotedOn, { [commentId]: newCommentsVotedOn[commentId] });
                Object.assign(userPageProfile.commentVoteVersions, { [commentId]: newCommentVoteVersions[commentId] });

                return userPageProfile;
            })
                .then(() => {

                    return this.storageCommentVotesRef.child(userId).child(commentId)
                        .putString('', firebase.storage.StringFormat.RAW, { customMetadata })
                        .catch(err => console.error(err));
                });
        });
    };

    updatePage = (mergeFn) => {

        return firestore.runTransaction(transaction => {

            const { projectId, pageId } = this.props;

            const pageRef = this.firestorePagesRef.doc(`${projectId}_${pageId}`);

            // This code may get re-run multiple times if there are conflicts.

            return transaction.get(pageRef).then(doc => {

                const page = doc.data() || this.constructor.defaultPage;

                try {

                    const update = Object.assign(
                        {},
                        this.constructor.defaultPage,
                        mergeFn(page),
                        { projectId, pageId }
                    );

                    transaction.set(pageRef, update);

                    return update;

                } catch(err) {

                    return Promise.reject(err);
                }
            });

        }).then(page => {

            return this.promiseState({ page });
        });

    };

    pinComment = commentId => {

        return this.updatePage(page => {

            page.pinnedComments[commentId] = '1';
            return page;
        });
    };

    unpinComment = commentId => {

        return this.updatePage(page => {

            page.pinnedComments = Object.keys(page.pinnedComments).reduce((pinnedComments, id) => {

                if (id !== commentId) {
                    pinnedComments[id] = '1';
                }

                return pinnedComments;

            }, {});

            return page;
        });
    };

    updatePageStatus = (status = null) => {

        return this.updatePage(page => {

            page.status = status || null;
            return page;
        });
    };

    apiCall = (url, options = {}, app = projectApp) => {

        return app.auth().currentUser.getIdToken(/* forceRefresh */ false)
            .then(idToken => {

                return fetch(`${this.props.apiUrl}/${this.props.projectId}${url}`, Object.assign({
                    method: 'POST',
                    mode: 'cors',
                    headers: {
                        'Content-Type': 'application/json; charset=utf-8',
                        'Authorization': `Bearer ${idToken}`
                    },
                }, options))
                    .then(response => {

                        return response.json().then(json => {

                            if(response.ok) {
                                return json.data;
                            }

                            throw new NetworkError(json.message, json.code);
                        });
                    });
            })

    };

    login = () => {

        const  { url, tlcParam } = this.props;

        return this.apiCall('/login', { body: JSON.stringify({ url, tlcParam })}, defaultApp)
            .then(response => {

                const {
                    token,
                    userIsModerator,
                    userIsBlocked,
                    userIsBlockedAnonymous,
                    isModerationPlus
                } = response;

                return projectApp.auth().setPersistence(firebase.auth.Auth.Persistence.NONE)
                    .then(() => projectApp.auth().signInWithCustomToken(token))
                    .then(() => ({
                        userIsModerator,
                        userIsBlocked,
                        userIsBlockedAnonymous,
                        isModerationPlus
                    }));
            });
    };

    blockUser = (commentId, days, isShadowBan = false) => {

        const { projectId, pageId } = this.props;
        const name = `${projectId}/${pageId}/${commentId}`;

        return this.apiCall('/blocked-user', { body: JSON.stringify({ days, name, isShadowBan }) });
    };

    unblockUser = commentId => {

        const { projectId, pageId } = this.props;
        const name = `${projectId}/${pageId}/${commentId}`;

        return this.apiCall('/blocked-user/unblock', { body: JSON.stringify({ name }) });
    };

    getBlockedUser = commentId => {

        const { projectId, pageId } = this.props;
        const name = `${projectId}/${pageId}/${commentId}`;

        return this.apiCall('/blocked-user/fetch', { body: JSON.stringify({ projectId, name }) });
    };

    logout = () => {

        const { email } = defaultApp.auth().currentUser;

        return projectApp.auth().signOut()
            .then(() => defaultApp.auth().signOut())
            .then(() => {

                const suffix = '.commentbox.io';

                if (this.props.sso && email.substr(-suffix.length) === suffix) {
                    this.sendMessage('sso-logout');
                }
            });
    };

    onSingleSignOn = () => {

        this.promiseState({ requestingUserPayload: true })
            .then(() => {

                this.sendMessage('sso-user-payload');
            });
    };

    singleSignOn = userPayload => {

        if (!this.props.sso) {
            return Promise.reject(new Error('SSO is not set.'));
        }

        return fetch(`${this.props.apiUrl}/${this.props.projectId}/sso`, {
            method: 'POST',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json; charset=utf-8'
            },
            body: JSON.stringify({
                userPayload
            })
        })
            .then(response => {

                return response.json()
                    .then(json => {

                        if(response.ok) {
                            return json.data;
                        }

                        throw new NetworkError(json.message, json.code);
                    });
            })
            .then(payload => {

                const { token, displayName, photoURL } = payload;

                return defaultApp.auth().setPersistence(firebase.auth.Auth.Persistence.SESSION)
                    .then(() => defaultApp.auth().signInWithCustomToken(token))
                    .then(() => this.promiseState({
                        ssoDisplayName: displayName,
                        ssoPhotoURL: photoURL,
                        requestingUserPayload: false
                    }));

            })
            .catch(err => {

                // fail silently, for now.

                return this.promiseState({
                    requestingUserPayload: false,
                    ssoError: err.message
                }).then(() => this.authStateChanged());
            });

    };

    get authUiConfig() {

        return {
            signInFlow: 'popup',
            signInOptions: [
                {
                    provider: firebase.auth.FacebookAuthProvider.PROVIDER_ID,
                    scopes: [
                        'public_profile',
                        'email'
                    ],
                    customParameters: this.state.reAuth ? {
                        auth_type: 'reauthenticate'
                    } : this.state.emailError ? {
                        auth_type: 'rerequest'
                    } : null
                },
                {
                    provider: firebase.auth.TwitterAuthProvider.PROVIDER_ID,
                    customParameters: this.state.reAuth ? {
                        force_login: true
                    } : null
                },
                {
                    provider: firebase.auth.GoogleAuthProvider.PROVIDER_ID,
                    customParameters: this.state.reAuth ? {
                        prompt: 'select_account'
                    } : null
                }
            ],
            credentialHelper: 'none',
            callbacks: {
                signInSuccessWithAuthResult: ({ additionalUserInfo: { isNewUser }, user }) => {

                    if (isNewUser && !user.emailVerified) {
                        user.sendEmailVerification({
                            url: 'https://app.commentbox.io?redirect='+encodeURIComponent(this.props.url)
                        });
                    }

                    const persistence = this.state.remember
                        ? firebase.auth.Auth.Persistence.LOCAL
                        : firebase.auth.Auth.Persistence.SESSION;

                    defaultApp.auth().setPersistence(persistence);

                    return false;
                },
                uiShown: () => {
                    this.sendResizeEvent();
                }
            }
        };
    }

    render() {

        if (this.state.loading) {
            return <div className="loader" />;
        }

        if (this.state.loadingError) {
            return <h3>{this.state.loadingError.message}</h3>;
        }

        const {
            sendResizeEvent,
            scrollToTlc,
            changeRemember,
            changeReAuth,
            changeUserPreferences,
            updateUserPageProfile,
            updateUserPreferences,
            updateUserProfile,
            postComment,
            editComment,
            removeComment,
            flagComment,
            commentVote,
            pinComment,
            unpinComment,
            blockUser,
            unblockUser,
            getBlockedUser,
            logout,
            getUser,
            onSingleSignOn,
            updatePageStatus,
            authUiConfig

        } = this;

        const props = Object.assign({
            sendResizeEvent,
            scrollToTlc,
            changeRemember,
            changeReAuth,
            changeUserPreferences,
            updateUserPageProfile,
            updateUserPreferences,
            updateUserProfile,
            postComment,
            editComment,
            removeComment,
            flagComment,
            commentVote,
            pinComment,
            unpinComment,
            blockUser,
            unblockUser,
            getBlockedUser,
            logout,
            getUser,
            onSingleSignOn,
            updatePageStatus,
            authUiConfig
        }, this.state, this.props);

        const classNames = ['app'];
        const style = {};
        if (this.props.backgroundColor) {
            style.backgroundColor = this.props.backgroundColor;
        }
        if (this.props.textColor) {
            style.color = this.props.textColor;
        }

        if (this.props.focusedInputTheme) {
            classNames.push(`focused-input-theme-${this.props.focusedInputTheme}`);
        }

        return (
            <div className={classNames.join(' ')} style={style}>
                <CommentBox {...props} />
                <div className="branding">
                    <a rel="nofollow" target="_blank" href="https://commentbox.io">
                        <img src={logo} />
                    </a>
                </div>
            </div>
        );
    }

    static calculateCommentVote(commentId, vote, commentVotes, commentsVotedOn) {

        const direction = vote === VOTE_UP ? 'up' : 'down';
        const oppositeDirection = direction === 'up' ? 'down' : 'up';
        const oppositeValue = direction === 'up' ? VOTE_DOWN : VOTE_UP;

        switch (commentsVotedOn[commentId]) {
            case vote:
                commentVotes[direction][commentId]--;
                commentsVotedOn[commentId] = VOTE_NONE;
                return VOTE_NONE;
            case (oppositeValue):
                commentVotes[oppositeDirection][commentId]--;
            default:
                commentVotes[direction][commentId] = (commentVotes[direction][commentId] || 0) + 1;
                commentsVotedOn[commentId] = vote;
                return vote;
        }
    }

    static best(positive, negative) {

        if ((positive + negative) === 0) {
            return 0;
        }

        return ((positive + 1.9208) / (positive + negative) - 1.96 * Math.sqrt((positive * negative) / (positive + negative) + 0.9604) / (positive + negative)) / (1 + 3.8416 / (positive + negative));
    }

    static get defaultProps() {

        const projectId = window.location.pathname.replace('/', '').split('/')[0];
        const parsedParams = parse(window.location.search.replace('?', ''));

        const {
            redirect,
            url,
            tlc_param: tlcParam,
            id: boxId,
            tlc: tlcCommentHash,
            background_color: backgroundColor,
            text_color: textColor,
            subtext_color: subtextColor,
            focused_input_theme: focusedInputTheme,
            sso,
            sso_user_payload: ssoUserPayload,
            sso_text: ssoText,
            sso_icon: ssoIcon,
            sso_color: ssoColor,
            sort_order: sortOrder

        } = parsedParams;

        if (redirect) {
            window.location = redirect;
            return;
        }

        if (!projectId) {
            throw new Error('No project ID found.');
        }
        if (!url) {
            throw new Error('No URL found.');
        }
        if (!tlcParam) {
            throw new Error('No tlc param found.');
        }

        if (!boxId) {
            throw new Error('No box ID found.');
        }

        const a = document.createElement('a');
        a.setAttribute('href', url.toLowerCase());
        if (a.pathname.length === 0 || a.pathname.charAt(a.pathname.length - 1) !== '/') {
            a.pathname = a.pathname + '/';
        }

        const pageId = SparkMD5.hash(a.href.replace(a.protocol, ''));

        return {
            apiUrl: `https://${process.env.REACT_APP_FIREBASE_PROJECT_ID}.appspot.com`,
            projectId,
            pageId,
            boxId,
            url,
            tlcParam,
            tlcCommentHash,
            backgroundColor,
            textColor,
            subtextColor,
            focusedInputTheme,
            sso: sso === '1',
            ssoUserPayload: ssoUserPayload || null,
            ssoText,
            ssoIcon,
            ssoColor,
            sortOrder
        };
    }

    static get defaultPage() {

        return {
            pinnedComments: {},
            status: null
        };
    }

    static get defaultUserPageProfile() {

        return {
            comments: {},
            flaggedComments: {},
            deletedComments: {},
            commentsVotedOn: {},
            commentVoteVersions: {}
        };
    }

    static get defaultUserPreferences() {

        return {
            anonymous: false,
            subscribe: false
        };
    }

    static get defaultState() {

        return {
            comments: [],
            commentVotes: {},
            editableComments: {},
            isLoggedIn: false,
            remember: true,
            reAuth: false,
            userId: null,
            userDisplayName: null,
            userPhotoURL: null,
            userProviderData: [],
            userIsModerator: false,
            userIsBlocked: null,
            userIsBlockedAnonymous: null,
            isModerationPlus: false,
            page: this.defaultPage,
            userPageProfile: this.defaultUserPageProfile,
            userPreferences: this.defaultUserPreferences,
            onUser: null,
            getUserPromise: null,
            loading: true,
            loadingError: null,
            emailError: false,
            error: null,
            scrolledToTlc: false,
            defaultUserPayload: null,
            requestingUserPayload: false
        };
    }
}

export default App;



// WEBPACK FOOTER //
// ./src/components/App.jsx